/**
 * Choosing which part of a long track to load, in track time.
 *
 * A manual load of a long file opens a picker rather than decoding the whole
 * thing — 20 minutes is already about 460 MB decoded, and this library holds
 * files of two hours. Everything the picker decides about *numbers* lives here
 * as pure functions: where the region opens, what a drag clamps to, which bar a
 * start snaps to. The modal is DOM plumbing around these.
 *
 * Every value in and out of this module is **track time**, the same frame as
 * `SegmentRequest.start`, beats, peaks and the planner. The deck converts to
 * buffer time in `audio/window.ts` and nowhere else.
 */

import type { Bounds } from "../automix/tails.js";

/**
 * The longest region a manual load will fetch.
 *
 * Deliberately not auto-mix's `maxSegmentSeconds` (7 minutes, and an operator
 * control): that cap exists so one long piece does not occupy the whole mix,
 * which is a different question from how much a person meant to listen to.
 * 20 minutes is also just under the ~500 MB decode the deck used to warn about,
 * so the picker replaces that warning rather than arguing with it.
 */
export const MANUAL_REGION_SECONDS = 20 * 60;

/**
 * The longest region actually available, given what the server will serve.
 *
 * `/api/audio` clamps an over-long request to `MAX_SEGMENT_SECONDS` and returns
 * 200 with a shorter body, so a picker that trusted its own constant would
 * offer twenty minutes, hand the deck a fifteen-minute buffer, and be wrong
 * about where the region ended. Neither side is incorrect alone; the number
 * has to cross the wire. `/api/health` carries it.
 */
export function availableCap(serverMaxSeconds: number | null | undefined): number {
  if (!serverMaxSeconds || !(serverMaxSeconds > 0)) return MANUAL_REGION_SECONDS;
  return Math.min(MANUAL_REGION_SECONDS, serverMaxSeconds);
}

/** The shortest region the picker will let you drag out. */
export const MIN_REGION_SECONDS = 30;

/** How near a bar line a dragged start must land to snap to it. */
export const SNAP_TOLERANCE_SECONDS = 1;

export interface Region {
  /** Track-time second the region begins at. */
  start: number;
  /** Length of the region, in seconds. */
  length: number;
}

export function regionEnd(region: Region): number {
  return region.start + region.length;
}

/** Whether a manually loaded track is long enough to need picking over. */
export function needsRegion(duration: number | null, cap = MANUAL_REGION_SECONDS): boolean {
  return (duration ?? 0) > cap;
}

/**
 * Keep a region inside the track and inside the cap.
 *
 * Length is clamped first and start second, so dragging the start to the far
 * end of a track shortens nothing — it stops the region at the end, which is
 * what the hand doing the dragging expects.
 */
export function clampRegion(region: Region, duration: number, cap = MANUAL_REGION_SECONDS): Region {
  if (!(duration > 0)) return { start: 0, length: 0 };

  const longest = Math.min(cap, duration);
  const shortest = Math.min(MIN_REGION_SECONDS, longest);
  const length = Math.min(Math.max(region.length, shortest), longest);
  const start = Math.min(Math.max(region.start, 0), duration - length);
  return { start, length };
}

/**
 * The nearest bar line within `tolerance`, or the position unchanged.
 *
 * Bars are counted from the first detected beat, exactly as the detail
 * waveform draws them: a reading aid, not a claim about where the downbeat is.
 */
export function snapToBar(
  seconds: number,
  beats: readonly number[],
  beatsPerBar = 4,
  tolerance = SNAP_TOLERANCE_SECONDS,
): number {
  if (beats.length <= beatsPerBar) return seconds;

  let best: number | null = null;
  for (let i = 0; i < beats.length; i += beatsPerBar) {
    const beat = beats[i] as number;
    if (best === null || Math.abs(beat - seconds) < Math.abs(best - seconds)) best = beat;
  }
  if (best === null || Math.abs(best - seconds) > tolerance) return seconds;
  return best;
}

/**
 * Where the picker opens: a capped region starting where the track starts
 * sounding, snapped to a bar.
 *
 * Not the random excerpt auto-mix would choose. Randomness is right when
 * nobody is looking; here somebody is, and an offer to start at the beginning
 * of the music is the one they can judge fastest.
 */
export function defaultRegion(
  bounds: Bounds,
  duration: number,
  beats: readonly number[] = [],
  cap = MANUAL_REGION_SECONDS,
  beatsPerBar = 4,
): Region {
  if (!(duration > 0)) return { start: 0, length: 0 };

  const start = Math.min(Math.max(bounds.start, 0), duration);
  const end = Math.min(Math.max(bounds.end, start), duration);
  // An unreadable or entirely silent bound gives a zero-length span; fall back
  // to the whole track rather than opening on a region of nothing.
  const audible = end - start > 0 ? end - start : duration;

  return clampRegion(
    { start: snapToBar(start, beats, beatsPerBar), length: Math.min(cap, audible) },
    duration,
    cap,
  );
}
